/**
 * Created by Abner Sui on 12/1/2020
 * -----------maintenance history---------------------
 * introduced template-bubble-service and injected it into each config component and validate for label name duplication in template-configuration-component by Simon Zhao on 12/10/2021
 */

import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { DynamicComponentService } from '../../services/dynamic-component.service';
import { ContextMenuConfig, ContextMenuShowAction } from '../../widgets/tam-context-menu/tam-context-menu.model';
import { ControlBrief, ControlConfigEvents, Template, TemplateComponentConfiguration, TemplateComponentConfigurationActions } from './template-configuration.model';
import { BriefConfig } from '../../widgets/widgets.model';
import { AppState } from './../../redux/index';
import { Store, select } from '@ngrx/store';
import { workflowUsedFieldsSelector } from './../../tamalelibs/redux/reducers/workflow.reducer';
import { FieldDefinition } from './../../tamalelibs/models/workflow.model';
import { TemplateBubbleService } from './template-bubble.service';
import { FieldConfig, FieldValidationError } from '../work-flow/field/field.model';
import { TaskDefinitionTemplateService } from './../work-flow/field/task-definition-template.service';
import { DiagramService } from '../../widgets/diagram/diagram.service';
import { WorkflowConfigService } from '../../tamalelibs/services/workflow-config.service';

@Component({
    selector: 'tam-template-configuration',
    templateUrl: './template-configuration.component.html',
    styleUrls: ['./template-configuration.component.scss'],
    providers: [TemplateBubbleService]
})
export class TemplateConfigurationComponent implements OnInit, OnDestroy {

    @Input() config: TemplateComponentConfiguration;

    @ViewChild('controlConfigContainer', { static: false, read: ViewContainerRef }) controlConfigContainer: ViewContainerRef;

    contextMenuConfig: ContextMenuConfig;
    selectedControl: Template;
    configComponent;
    controlConfigurationConfig: BriefConfig = new BriefConfig();
    options: BriefConfig = new BriefConfig();

    private _configSubscription: Subscription;
    private _destroySubscriptions: Array<Subscription> = [];
    private _usedFieldLabels = new Map<string, string>();

    constructor(
        private _componentService: DynamicComponentService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _diagramService: DiagramService,
        private _store: Store<AppState>,
        private _bubbleService: TemplateBubbleService,
        private _workflowConfigService: WorkflowConfigService,
    ) {
        this._bubbleService.diagramService = this._diagramService;
        this._bubbleService.workflowConfigService = this._workflowConfigService;
    }

    /**
     * event handler for the control order changed event.
     *
     * @param {*} e
     * @memberof TemplateConfigurationComponent
     */
    afterControlOrderChanged(e: any) {
        this.config.configChanged = true;
    }

    ngOnInit(): void {
        this._destroySubscriptions.push(
            this.controlConfigurationConfig.feedbackSubject$.subscribe(event => this._onEvent(event)),
            this.config.config.actionSubject$.subscribe(actions => {
                if (actions.type === TemplateComponentConfigurationActions.LEAVE_TEMPLATE_MENU) {
                    if (this.selectedControl && this.configComponent) {
                        const index: number = this.config.invalidControlId.indexOf(this.selectedControl.id);
                        if (index > -1) {
                            this.config.invalidControlId.splice(index, 1);
                            this.config.invalidDetail.delete(this.selectedControl.id);
                        }
                        if (!this.configComponent.instance.validate()) {
                            this.config.invalidControlId.push(this.selectedControl.id);
                            this.config.invalidDetail.set(this.selectedControl.id, this.configComponent.instance.getInvalidDetail());
                        }
                    }
                }
            }),
            this._store.pipe(
                select(workflowUsedFieldsSelector),
            ).subscribe((res: Map<string, FieldDefinition>) => {
                this._usedFieldLabels.clear();
                res.forEach((value, key, map) => {
                    if (value) {
                        this._usedFieldLabels.set(key, value.name);
                    }
                });
            }),
            this._bubbleService.fieldLabelChangeSubject$.subscribe(fieldCfg => {
                const ctrlWithLabelConfliction = [];
                const validControlIds = [];
                const ctrlWithoutLabelConfliction = [];
                const labelNameMap = new Map<string, string[]>();
                const invalidMap = TaskDefinitionTemplateService.getInvalidControlDetail(this.config.template, validControlIds, labelNameMap);
                invalidMap.forEach((fieldError, controlId, keyValuePair) => {
                    if (fieldError.isLableDuplicatedInvalid) {
                        if (!ctrlWithLabelConfliction.includes(controlId)) {
                            ctrlWithLabelConfliction.push(controlId);
                        }
                    } else {
                        ctrlWithLabelConfliction.push(controlId);
                    }
                });
                ctrlWithoutLabelConfliction.push(...validControlIds);
                // A function responsible for manipulation of config's memebers recording invalid fields.
                const updateLabelConflictionForFieldsFunc = (ids: string[], isConflicted: boolean) => {
                    if (!ids || ids.length === 0) {
                        return;
                    }

                    if (isConflicted) {
                        // update given fields as label-conflicted.
                        ids.forEach(id => {
                            if (this.config.invalidControlId.indexOf(id) < 0) {
                                this.config.invalidControlId.push(id);
                            }

                            let invalidDetailItem = this.config.invalidDetail.get(id);
                            if (!invalidDetailItem) {
                                invalidDetailItem = new FieldValidationError();
                                this.config.invalidDetail.set(id, invalidDetailItem);
                            }
                            invalidDetailItem.isLableDuplicatedInvalid = true;
                        });
                    } else {
                        // update given fields as no-label-conflicted.
                        ids.forEach(id => {
                            const invalidDetailItem = this.config.invalidDetail.get(id);
                            if (invalidDetailItem) {
                                invalidDetailItem.isLableDuplicatedInvalid = false;
                                if (FieldValidationError.isFieldValid(invalidDetailItem)) {
                                    this.config.invalidDetail.delete(id);
                                    const idIndex = this.config.invalidControlId.indexOf(id);
                                    if (idIndex >= 0) {
                                        this.config.invalidControlId.splice(idIndex, 1);
                                    }
                                }
                            }
                        });
                    }
                };

                labelNameMap.forEach((idArray, label, map) => {
                    if (idArray.length > 1) {
                        // found more than one field sharing a same label.

                        if (idArray.includes(fieldCfg.id)) {
                            // trigger control caused a conflict, mark it as duplicated one rather than late comers.
                            updateLabelConflictionForFieldsFunc([fieldCfg.id], true);
                        } else {
                            const latecomers = idArray.slice(1, idArray.length);
                            updateLabelConflictionForFieldsFunc(latecomers, true);
                        }
                    } else if (idArray.length === 1) {
                        // the field has no label name conflict.
                        updateLabelConflictionForFieldsFunc(idArray, false);
                    }
                });

                this._syncFieldConfigLabelConfictStatus(fieldCfg);
            })
        );
        this._initContextMenu();
    }

    ngOnDestroy(): void {
        this._destroySubscriptions.forEach(item => item.unsubscribe());
    }

    onAddControl(event, item: ControlBrief, data?: any): void {
        const result = this.config.service.addControl(item, this.config.template);
        result.config = this.controlConfigurationConfig;
        this.config.configChanged = true;
        this.onSelectChange(result);
    }

    onContextMenu(event): void {
        this._changeDetectorRef.detectChanges();
        this.contextMenuConfig.show$.next(new ContextMenuShowAction(event, ''));
    }

    onDeleteControl(event): void {
        const index = this.config.invalidControlId.indexOf(event.id);
        if (index > -1) {
            this.config.invalidControlId.splice(index, 1);
            this.config.invalidDetail.delete(event.id);
        }
        this.config.configChanged = true;
        this.config.service.deleteControl(event, this.config.template);
    }

    onSelectChange(control: Template): void {
        if (this.selectedControl && control && this.selectedControl.id === control.id) {
            return;
        }
        if (this.selectedControl && this.configComponent) {
            const index: number = this.config.invalidControlId.indexOf(this.selectedControl.id);
            if (index > -1) {
                this.config.invalidControlId.splice(index, 1);
                this.config.invalidDetail.delete(this.selectedControl.id);
            }

            if (!this.configComponent.instance.validate()) {
                this.config.invalidControlId.push(this.selectedControl.id);
                this.config.invalidDetail.set(this.selectedControl.id, this.configComponent.instance.getInvalidDetail());
            }
            this.controlConfigContainer.clear();
        }
        if (this._configSubscription && !this._configSubscription.closed) {
            this._configSubscription.unsubscribe();
        }
        this.selectedControl = control;
        if (this.selectedControl && this.selectedControl.configComponent) {
            this.selectedControl.config = this.controlConfigurationConfig;
            if (this.config.invalidControlId.indexOf(this.selectedControl.id) > -1) {
                this.selectedControl.showValidateInfo = true;
            }
            this._syncFieldConfigLabelConfictStatus(this.selectedControl);

            this.configComponent = this._componentService.loadComponent(this.controlConfigContainer, this.selectedControl.configComponent, false);
            this.configComponent.instance.config = this.selectedControl;

            if (this.selectedControl.config) {
                this._configSubscription = this.selectedControl.config.feedbackSubject$.subscribe(event => {
                    if (event.type === ControlConfigEvents.VALUE_CHANGE) {
                        this.config.configChanged = true;
                    }
                });
            }
        }
    }

    /**
     * gets the control id by the field defintion id.
     * @param fieldId the given field definition id.
     * @returns the control id related to the given field id.
     */
    private _getControlIdByFieldId(fieldId: string) {
        let ctrlId;
        for (let r = 0; r < this.config.template.contains.length; r++) {
            const row = this.config.template.contains[r];
            for (let c = 0; c < row.length; c++) {
                const control: FieldConfig = row[c];
                if (control.fieldDefinitionId === fieldId) {
                    ctrlId = control.id;
                    break;
                }
            }
        }

        if (ctrlId) {
            return ctrlId;
        } else {
            throw new Error('Failed to find the control id for the given field definition id!');
        }
    }

    private _initContextMenu(): void {
        this.contextMenuConfig = new ContextMenuConfig();
        this.contextMenuConfig.items = [];
        this._destroySubscriptions.push(this.contextMenuConfig.events$.subscribe(event => this._onMenuSelect(event)));
    }

    private _onEvent(event): void {
        if (event.type === ControlConfigEvents.VALUE_CHANGE) {
            this.config.configChanged = true;
        } else if (event.type === ControlConfigEvents.CHANGE_WIDTH) {
            this.options.actionSubject$.next(event);
        }
    }

    private _onMenuSelect(event): void {
        const type = +event.payload.id;
        switch (type) {
            case 0:
                break;
            case 1:
                break;
            default:
                break;
        }
    }

    /**
    * Syncs the label conflict result from the property invalidDetail to the field config which is applied on the config component.
    * @param fieldCfg the field configuration.
    */
    private _syncFieldConfigLabelConfictStatus(fieldCfg: Template) {
        const fieldInvalidDetail = this.config.invalidDetail.get(fieldCfg.id);
        if (fieldInvalidDetail && fieldInvalidDetail.isLableDuplicatedInvalid) {
            fieldCfg.isDuplicated = true;
        } else {
            fieldCfg.isDuplicated = false;
        }
    }

}
